import AgreementSignModal from '@/components/config_components/AgreementSignModal/AgreementSignModal.vue';

import {
  ACTIVATION_BADGE_CLASSES,
  ANSWER_DOT_CLASSES,
  get_journey_dot_style,
  get_mapped_value,
  PROGRESS_COLORS,
  RISK_BADGE_CLASSES
} from '@/views/onboarding_system_views/onboarding_ui_helpers';

import {
  build_agreement_rows,
  build_contact_rows,
  build_profile_rows,
  build_questionnaire_answers,
  build_summary_rows,
  build_stage_timeline,
  fetch_onboarding_cases,
  get_journey_stats,
  hydrate_case_detail
} from '@/services/onboarding_journey_service';
import {
  tablePaginationComputed,
  tablePaginationMethods,
  tablePaginationState
} from '@/services/table_pagination_service';

const SUPPLIER_STATE = {
    active_filter: 'all',
    selected_supplier: null,
    is_loading_cases: false,
    cases_error: '',
    summary_rows: [],
    profile_rows: [],
    contact_rows: [],
    agreement_rows: [],
    is_detail_fullscreen: false,
    sign_modal: {
      visible: false,
      agreement_title: 'Supplier agreement',
      signatory_name: '',
      document_id: 'DOC-SUPPLIER-2026'
    },
    active_detail_tab: 'summary',
    detail_tabs: [
      { id: 'summary', label: 'Summary', icon: 'bi-grid' },
      { id: 'profile', label: 'Profile', icon: 'bi-building' },
      { id: 'questionnaire', label: 'Questionnaire', icon: 'bi-list-check' },
      { id: 'agreements', label: 'Agreements', icon: 'bi-folder', count: '2' }
    ],
    stage_timeline: [],
    questionnaire_answers: [],
    stats: [
      { label: 'Active', value: '0', detail: 'Onboardings', tone: 'text-black' },
      { label: 'Blocked', value: '0', detail: 'Resolve to proceed', tone: 'text-danger' },
      { label: 'Ready', value: '0', detail: 'Activate today', tone: 'text-success' },
      { label: 'Avg time', value: '-', detail: 'To activation', tone: 'text-black' }
    ],
    journey_filters: [
      { label: 'All', count: '18', tone: 'all' },
      { label: 'Supplier', tone: 'supplier' },
      { label: 'Employee', tone: 'employee' },
      { label: 'Merchant', tone: 'merchant' },
      { label: 'Partner', tone: 'partner' },
      { label: 'Intern', tone: 'intern' },
      { label: 'Client', tone: 'client' }
    ],
    supplier_cases: []
  };

export default {
  name: 'OnboardingManageSupplier',
  components: {
    AgreementSignModal
  },
  data: () => ({
    ...SUPPLIER_STATE,
    ...tablePaginationState()
  }),
  created() {
    this.load_supplier_cases();
  },
  computed: {
    filtered_supplier_cases() {
      return this.filter_table_rows(this.filter_cases_by_journey(this.supplier_cases, this.active_filter));
    },
    table_total_rows() {
      return this.filtered_supplier_cases.length;
    },
    paginated_supplier_cases() {
      return this.paginate_table_rows(this.filtered_supplier_cases);
    },
    ...tablePaginationComputed
  },
  methods: {
    async load_supplier_cases() {
      this.is_loading_cases = true;
      this.cases_error = '';

      try {
        const cases = await fetch_onboarding_cases('supplier');
        this.supplier_cases = cases;
        this.stats = get_journey_stats(cases, 'supplier');
      } catch (error) {
        this.cases_error = error?.response?.data?.message || error?.message || 'Unable to load supplier onboardings.';
        this.supplier_cases = [];
        this.stats = get_journey_stats([], 'supplier');
      } finally {
        this.is_loading_cases = false;
      }
    },
    case_matches_filter(case_item, tone) {
      if (tone === 'all') return true;

      const normalized_tone = String(tone || '').trim().toLowerCase();
      const journey_values = [
        case_item?.journey_tone,
        case_item?.journey_key,
        case_item?.journey,
        case_item?.entity_type_name,
        case_item?.current_entity_type_name
      ]
        .map((value) => String(value || '').trim().toLowerCase())
        .filter(Boolean);

      if (journey_values.includes(normalized_tone)) return true;
      return journey_values.some((value) => value.includes(normalized_tone));
    },
    filter_cases_by_journey(cases = [], tone = 'all') {
      return cases.filter((case_item) => this.case_matches_filter(case_item, tone));
    },
    set_active_filter(tone) {
      this.active_filter = tone;
      this.reset_table_page();
    },
    ...tablePaginationMethods,
    async open_supplier_detail(case_item) {
      this.selected_supplier = case_item;
      this.active_detail_tab = 'summary';
      this.is_detail_fullscreen = false;
      this.summary_rows = build_summary_rows(case_item);
      this.profile_rows = build_profile_rows(case_item);
      try {
        const hydrated_case = await hydrate_case_detail(case_item);
        const summary_rows = build_summary_rows(hydrated_case);
        const profile_rows = build_profile_rows(hydrated_case);
        const contact_rows = build_contact_rows(hydrated_case);
        const agreement_rows = build_agreement_rows(hydrated_case);
        const stage_timeline = build_stage_timeline(hydrated_case);
        const questionnaire_answers = build_questionnaire_answers(hydrated_case);

        this.selected_supplier = hydrated_case;

        if (summary_rows.length) {
          this.summary_rows = summary_rows;
        }

        if (profile_rows.length) {
          this.profile_rows = profile_rows;
        }

        if (contact_rows.length) {
          this.contact_rows = contact_rows;
        }

        if (agreement_rows.length) {
          this.agreement_rows = agreement_rows;
        }

        if (stage_timeline.length) {
          this.stage_timeline = stage_timeline;
        }

        if (questionnaire_answers.length) {
          this.questionnaire_answers = questionnaire_answers;
        }
      } catch {
        this.selected_supplier = case_item;
      }
    },
    close_supplier_detail() {
      this.selected_supplier = null;
      this.is_detail_fullscreen = false;
    },
    set_detail_tab(tab_id) {
      this.active_detail_tab = tab_id;
    },
    toggle_detail_fullscreen() {
      this.is_detail_fullscreen = !this.is_detail_fullscreen;
    },
    open_sign_modal(agreement_title = 'Supplier agreement', signatory_name = '') {
      this.sign_modal = {
        visible: true,
        agreement_title,
        signatory_name: signatory_name || this.selected_supplier?.name || '',
        document_id: 'DOC-SUPPLIER-2026'
      };
    },
    close_sign_modal() {
      this.sign_modal.visible = false;
    },
    complete_signature() {
      this.close_sign_modal();
    },
    journey_dot_style(tone) {
      return get_journey_dot_style(tone);
    },
    progress_bar_color(tone) {
      return get_mapped_value(PROGRESS_COLORS, tone, '#416f6e');
    },
    risk_badge_class(tone) {
      return get_mapped_value(RISK_BADGE_CLASSES, tone, 'bg-secondary-subtle text-secondary-emphasis');
    },
    activation_badge_class(tone) {
      return get_mapped_value(ACTIVATION_BADGE_CLASSES, tone, 'bg-secondary-subtle text-secondary-emphasis');
    },
    answer_dot_class(tone) {
      return get_mapped_value(ANSWER_DOT_CLASSES, tone, 'bg-secondary');
    },
    journey_filter_count(filter) {
      return this.filter_cases_by_journey(this.supplier_cases, filter.tone).length;
    }
  }
};


